/**
 * permissions.js — MIROIR de backend/security/rbac.py (ROLE_PERMISSIONS).
 * Garder les deux SYNCHRONISÉS. Le backend reste la vraie barrière ; ceci sert à
 * masquer ce qui est interdit (éviter les culs-de-sac 403) et gater les boutons.
 *
 *   import { can, isAssocie } from '../lib/permissions';
 *   can(user?.role, 'factures', 'read')   // false pour Avocat
 *   isAssocie(user?.role)                  // accès finance/réglages
 */
const CRUD = ['create', 'read', 'update', 'delete'];

// Normalise un rôle : minuscule + sans accents ('Associé' -> 'associe').
const strip = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const FULL = {
  clients: CRUD, intervenants: CRUD, dossiers: CRUD,
  factures: CRUD, finance: ['read'], contrats: CRUD, echeances: CRUD, audiences: CRUD,
  taches: CRUD, contacts: CRUD, temps: CRUD, documents: CRUD,
  users: CRUD, reports: ['read'], settings: ['read', 'update'],
  gestion: CRUD,   // dépenses / employés / salaires — associés uniquement
};

const PERMS = {
  administrateur: FULL,
  associe: FULL,
  avocat: {
    clients: ['create', 'read', 'update'], intervenants: ['read'], dossiers: ['create', 'read', 'update'],
    factures: [], finance: [], contrats: ['create', 'read', 'update'],
    echeances: ['create', 'read', 'update'], audiences: ['create', 'read', 'update'],
    taches: CRUD, temps: CRUD, contacts: ['create', 'read', 'update'],
    documents: ['create', 'read', 'update', 'delete'],
    users: ['read'], reports: [], settings: [],
  },
  assistant: {
    clients: ['create', 'read', 'update'], intervenants: ['read'], dossiers: ['create', 'read', 'update'],
    factures: [], finance: [], contrats: ['read'],
    echeances: ['create', 'read', 'update'], audiences: ['create', 'read', 'update'],
    taches: ['create', 'read', 'update'], temps: ['create', 'read', 'update'],
    contacts: ['create', 'read', 'update'], documents: ['create', 'read', 'update'],
    users: ['read'], reports: [], settings: [],
  },
  stagiaire: {
    clients: ['read'], intervenants: ['read'], dossiers: ['read'],
    factures: [], finance: [], contrats: ['read'],
    echeances: ['read'], audiences: ['read'],
    taches: ['create', 'read', 'update'], temps: ['create', 'read', 'update'],
    contacts: ['read'], documents: ['create', 'read'],
    users: [], reports: [], settings: [],
  },
};

/** Le rôle peut-il (action) sur (resource) ? SuperAdmin = bypass total. */
export function can(role, resource, action) {
  const r = strip(role);
  if (r === 'superadmin') return true;
  const perms = PERMS[r];
  if (!perms) return false;
  return (perms[resource] || []).includes(action);
}

/** Accès « finance & administration » (Associé / Administrateur uniquement). */
export function isAssocie(role) {
  return ['administrateur', 'associe'].includes(strip(role));
}

/** SuperAdmin (plateforme). */
export function isSuperAdmin(role, isSuperFlag) {
  return strip(role) === 'superadmin' || !!isSuperFlag;
}

export const ROLE_LABELS = {
  Administrateur: 'Administrateur',
  Associé: 'Associé',
  Avocat: 'Avocat (collaborateur)',
  Assistant: 'Assistant de direction',
  Stagiaire: 'Stagiaire',
};
